import React from "react";
import { Table, Button, Alert, Form, Spinner } from "react-bootstrap";
import { useGetStoreSuppliesQuery } from "../../slices/storeSuppliesApiSlice";
import { useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const StoreSupplyInvoiceSummary = () => {
  const { data, isLoading, error } = useGetStoreSuppliesQuery();
  
  const [showTable, setShowTable] = useState(false);
  const [firstInvoice, setFirstInvoice] = useState("");
  const [lastInvoice, setLastInvoice] = useState("");
  const [validationError, setValidationError] = useState("");
  const [showZeroOnly, setShowZeroOnly] = useState(false);
  const [searchInvoice, setSearchInvoice] = useState("");
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [missingInvoices, setMissingInvoices] = useState([]);
  const [groupedByMonth, setGroupedByMonth] = useState({});

  const toggleInvoiceDetails = (invoice) => {
    setExpandedInvoice((prev) => (prev === invoice ? null : invoice));
  };

  const generateSummary = () => {
    const start = Number(firstInvoice);
    const end = Number(lastInvoice);

    if (isNaN(start) || isNaN(end)) {
      setValidationError("Please enter valid invoice numbers.");
      setShowTable(false);
      return;
    }

    if (start >= end) {
      setValidationError("First invoice number must be smaller than the last.");
      setShowTable(false);
      return;
    }

    setValidationError("");

    const invoiceMap = {};
    const foundInvoices = new Set();

    data?.storeSupplies?.forEach((storeSupply) => {
      const invoice = storeSupply.note?.trim();
      const supplier = storeSupply.supplierDetails?.name?.trim() || "Unknown";
      const date = new Date(storeSupply.date).toLocaleDateString();

      if (!invoice || isNaN(Number(invoice))) return;

      const invoiceNum = Number(invoice);
      if (invoiceNum < start || invoiceNum > end) return;

      foundInvoices.add(invoice);

      if (!invoiceMap[invoice]) {
        invoiceMap[invoice] = {
          qty: 0,
          cost: 0,
          profit: 0,
          supplier,
          date,
          products: [],
        };
      }

      storeSupply.storeSupplyItems.forEach((item) => {
        invoiceMap[invoice].qty += item.qty;
        invoiceMap[invoice].cost += item.itemCost;
        invoiceMap[invoice].profit += item.profit;
        invoiceMap[invoice].products.push({ name: item.name, qty: item.qty });
      });
    });

    const missing = [];
    for (let i = start; i <= end; i++) {
      if (!foundInvoices.has(i.toString())) {
        missing.push(i.toString());
      }
    }

    
    const grouped = {};
Object.entries(invoiceMap).forEach(([invoice, entry]) => {
  const monthKey = entry.date
    ? new Date(entry.date).toLocaleString("default", { month: "long", year: "numeric" })
    : "Unspecified";

  if (!grouped[monthKey]) {
    grouped[monthKey] = [];
  }
  grouped[monthKey].push({ invoice, ...entry });
});

setGroupedByMonth(grouped);


    setMissingInvoices(missing);
    setShowTable(true);
  };

  const exportToExcel = () => {
  const rows = [];

  Object.entries(groupedByMonth).forEach(([month, entries]) => {
    rows.push({}, { Month: month, Invoice: "", Date: "", Supplier: "", Quantity: "", Cost: "", Profit: "" });

    entries
      .filter((entry) =>
        entry.invoice.toLowerCase().includes(searchInvoice.toLowerCase()) &&
        (!showZeroOnly || (entry.qty === 0 && entry.cost === 0 && entry.profit === 0))
      )
      .sort((a, b) => Number(a.invoice) - Number(b.invoice))
      .forEach((entry) => {
        rows.push({
          Month: month,
          Invoice: entry.invoice,
          Date: entry.date,
          Supplier: entry.supplier,
          Quantity: entry.qty,
          Cost: entry.cost,
          Profit: entry.profit,
        });
      });

    const monthTotalQty = entries.reduce((sum, e) => sum + e.qty, 0);
    const monthTotalCost = entries.reduce((sum, e) => sum + e.cost, 0);
    const monthTotalProfit = entries.reduce((sum, e) => sum + e.profit, 0);

    rows.push({
      Month: month,
      Invoice: "Monthly Total",
      Date: "",
      Supplier: "",
      Quantity: monthTotalQty,
      Cost: monthTotalCost,
      Profit: monthTotalProfit,
    });
  });

  if (missingInvoices.length > 0) {
    rows.push({}, { Month: "", Invoice: "Missing Invoice Numbers" });
    missingInvoices.forEach((invoice) => {
      rows.push({ Invoice: invoice });
    });
  }

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Invoice Summary");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, `StoreSupply_Invoice_Summary_${firstInvoice}_to_${lastInvoice}.xlsx`);
};

return (
  <div className="container mt-4">
    <h2 className="text-center mb-4">MiniSupply Invoice Summary</h2>

    <Form className="mb-3 d-flex justify-content-center flex-wrap">
      <Form.Group className="mx-2">
        <Form.Label>First Invoice Number:</Form.Label>
        <Form.Control
          type="number"
          value={firstInvoice}
          onChange={(e) => setFirstInvoice(e.target.value)}
        />
      </Form.Group>
      <Form.Group className="mx-2">
        <Form.Label>Last Invoice Number:</Form.Label>
        <Form.Control
          type="number"
          value={lastInvoice}
          onChange={(e) => setLastInvoice(e.target.value)}
        />
      </Form.Group>
      <Form.Group className="mx-2">
        <Form.Label>Search Invoice:</Form.Label>
        <Form.Control
          type="text"
          value={searchInvoice}
          onChange={(e) => setSearchInvoice(e.target.value)}
          placeholder="e.g. 1025"
        />
      </Form.Group>
      <div className="d-flex align-items-end mx-2">
        <Button variant="primary" onClick={generateSummary} disabled={isLoading}>
          {isLoading ? <Spinner animation="border" size="sm" /> : "Generate Summary"}
        </Button>
      </div>
      <div className="d-flex align-items-end mx-2">
        <Button
          variant={showZeroOnly ? "warning" : "outline-warning"}
          onClick={() => setShowZeroOnly(!showZeroOnly)}
        >
          {showZeroOnly ? "Show All" : "Show Only Zero Totals"}
        </Button>
      </div>
    </Form>

    {validationError && (
      <Alert variant="danger" className="text-center">
        {validationError}
      </Alert>
    )}

    {error && (
      <Alert variant="danger" className="text-center">
        {error?.data?.message || "Error loading storeSupplies."}
      </Alert>
    )}

    {showTable && (
      <>
        {Object.entries(groupedByMonth).map(([month, entries]) => (
  <div key={month} className="mb-5">
    <h5 className="text-center">{month}</h5>
    <Table striped bordered hover responsive>
      <thead className="text-center">
        <tr>
          <th>Invoice</th>
          <th>Date</th>
          <th>Supplier</th>
          <th>Total Quantity</th>
          <th>Total Cost</th>
          <th>Total Profit</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody className="text-center">
        {entries
          .filter((entry) =>
            entry.invoice.toLowerCase().includes(searchInvoice.toLowerCase()) &&
            (!showZeroOnly || (entry.qty === 0 && entry.cost === 0 && entry.profit === 0))
          )
          .sort((a, b) => Number(a.invoice) - Number(b.invoice))
          .map((entry) => (
            <React.Fragment key={entry.invoice}>
              <tr>
                <td>{entry.invoice}</td>
                <td>{entry.date}</td>
                <td>{entry.supplier}</td>
                <td>{entry.qty}</td>
                <td>${entry.cost.toFixed(2)}</td>
                <td>${entry.profit.toFixed(2)}</td>
                <td>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => toggleInvoiceDetails(entry.invoice)}
                  >
                    {expandedInvoice === entry.invoice ? "Hide Products" : "Show Products"}
                  </Button>
                </td>
              </tr>
              {expandedInvoice === entry.invoice && (
                <tr>
                  <td colSpan="7">
                    <ul className="mb-0">
                      {entry.products.map((product, idx) => (
                        <li key={idx}>
                          {product.name} — Qty: {product.qty}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        <tr className="fw-bold">
          <td colSpan={3}>Monthly Total</td>
          <td>{entries.reduce((sum, e) => sum + e.qty, 0)}</td>
          <td>${entries.reduce((sum, e) => sum + e.cost, 0).toFixed(2)}</td>
          <td>${entries.reduce((sum, e) => sum + e.profit, 0).toFixed(2)}</td>
          <td></td>
        </tr>
      </tbody>
    </Table>
  </div>
))}

        {missingInvoices.length > 0 && (
          <div className="mt-5">
            <h5 className="text-center text-danger">Missing Invoice Numbers</h5>
            <Table striped bordered hover responsive>
              <thead className="text-center">
                <tr>
                  <th>Invoice Number</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {missingInvoices.map((invoice) => (
                  <tr key={invoice}>
                    <td>{invoice}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <div className="text-center mt-3">
          <Button variant="success" onClick={exportToExcel}>
            Export to Excel
          </Button>
        </div>
      </>
    )}
  </div>
  
);
};
export default StoreSupplyInvoiceSummary;
